import {
  Badge,
  Box,
  Card,
  CardBody,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  Progress,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  CalendarIcon,
  CheckCircleIcon,
  InfoOutlineIcon,
  StarIcon,
  TimeIcon,
  WarningIcon,
} from "@chakra-ui/icons";
import { formatDate } from "../utils/formatDate";

export const MainPackageCard = ({ mainPackage, texts }) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const titleColor = useColorModeValue("gray.800", "white");
  if (!mainPackage) return null;
  const statusColor = mainPackage?.IsApproved ? "green" : "orange";
  const statusIcon = mainPackage?.IsApproved ? CheckCircleIcon : TimeIcon;
  const statusText = mainPackage?.IsApproved
    ? texts?.completed
    : texts?.pending;
  const isExpired =
    mainPackage?.EndDate && new Date(mainPackage.EndDate) < new Date();
  const showWarning = isExpired && !mainPackage.IsApproved;
  const accentColor = showWarning
    ? "red"
    : statusColor === "green"
      ? "green"
      : "orange";

  return (
    <Card
      variant="outline"
      borderWidth="2px"
      borderColor={`${accentColor}.200`}
      bg={cardBg}
      shadow="xl"
      _hover={{
        shadow: "2xl",
        transform: "translateY(-4px)",
        borderColor: `${accentColor}.300`,
      }}
      transition="all 0.3s"
      position="relative"
      overflow="hidden"
      borderRadius="2xl"
    >
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        height="6px"
        bgGradient={`linear(to-r, ${accentColor}.400, ${accentColor}.600)`}
      />
      <Box
        position="absolute"
        top="-50px"
        right="-50px"
        width="200px"
        height="200px"
        borderRadius="full"
        bg={`${accentColor}.50`}
        opacity="0.1"
      />
      <CardBody p={6}>
        <VStack align="start" spacing={5}>
          <Flex justify="space-between" w="full" align="center">
            <HStack spacing={4}>
              <Flex
                w="48px"
                h="48px"
                bg={`${accentColor}.100`}
                borderRadius="xl"
                align="center"
                justify="center"
              >
                <Icon as={StarIcon} color={`${accentColor}.600`} boxSize={6} />
              </Flex>
              <Box>
                <Text fontSize="lg" fontWeight="bold" color={titleColor}>
                  {mainPackage?.PackageName || texts?.mainPackage}
                </Text>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  {texts?.mainPackageDescription}
                </Text>
              </Box>
            </HStack>
            <Badge
              colorScheme={showWarning ? "red" : statusColor}
              fontSize="md"
              px={5}
              py={2}
              borderRadius="full"
              variant="solid"
              shadow="md"
              transform="scale(1.05)"
            >
              <HStack spacing={2}>
                <Icon as={statusIcon} boxSize={4} />
                <Text fontWeight="bold">
                  {showWarning ? texts?.expired : statusText}
                </Text>
              </HStack>
            </Badge>
          </Flex>
          <Divider borderColor="gray.200" />
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
            gap={6}
            w="full"
          >
            <GridItem>
              <VStack align="start" spacing={3}>
                <HStack>
                  <Icon as={InfoOutlineIcon} color="gray.400" />
                  <Text fontWeight="medium" color="gray.600">
                    {texts?.status}
                  </Text>
                </HStack>
                <Progress
                  value={mainPackage?.IsApproved ? 100 : 50}
                  colorScheme={accentColor}
                  size="lg"
                  borderRadius="full"
                  height="20px"
                  hasStripe
                  isAnimated={!mainPackage?.IsApproved}
                  shadow="sm"
                />
                <HStack justify="space-between" w="full">
                  <Text fontSize="sm" color="gray.500">
                    0%
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    100%
                  </Text>
                </HStack>
              </VStack>
            </GridItem>
            <GridItem>
              <VStack align="start" spacing={3}>
                <HStack>
                  <Icon as={CalendarIcon} color="gray.400" />
                  <Text fontWeight="medium" color="gray.600">
                    {texts?.endDate}
                  </Text>
                </HStack>
                <Card
                  bg={
                    !mainPackage?.EndDate
                      ? "blue.50"
                      : showWarning
                        ? "red.50"
                        : "gray.50"
                  }
                  p={4}
                  w="full"
                  borderRadius="xl"
                  border="1px"
                  borderColor={
                    !mainPackage?.EndDate
                      ? "blue.200"
                      : showWarning
                        ? "red.200"
                        : "gray.200"
                  }
                >
                  <HStack justify="space-between">
                    {mainPackage?.EndDate ? (
                      <>
                        <Text
                          fontSize="lg"
                          fontWeight="bold"
                          color={showWarning ? "red.600" : "gray.700"}
                        >
                          {formatDate(mainPackage.EndDate)}
                        </Text>
                        {showWarning && (
                          <Icon as={WarningIcon} color="red.500" boxSize={5} />
                        )}
                      </>
                    ) : (
                      <>
                        <Text fontSize="lg" fontWeight="bold" color="blue.600">
                          {texts?.pending || "Pending.."}
                        </Text>
                        <Badge
                          colorScheme="blue"
                          variant="subtle"
                          fontSize="sm"
                        >
                          ⏳ {texts?.pending || "Pending"}
                        </Badge>
                      </>
                    )}
                  </HStack>
                  {mainPackage?.EndDate && showWarning && (
                    <Text fontSize="sm" color="red.500" mt={2}>
                      {texts?.expired}
                    </Text>
                  )}
                  {!mainPackage?.EndDate && (
                    <Text fontSize="sm" color="blue.500" mt={2}>
                      {texts?.endDateNotSet || "End date not yet specified"}
                    </Text>
                  )}
                </Card>
              </VStack>
            </GridItem>
          </Grid>
        </VStack>
      </CardBody>
    </Card>
  );
};

export const SubPackageCard = ({ subPackage, texts }) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const titleColor = useColorModeValue("gray.800", "white");
  const statusColor = subPackage.IsApproved ? "green" : "orange";
  const statusIcon = subPackage.IsApproved ? CheckCircleIcon : TimeIcon;
  const statusText = subPackage.IsApproved ? texts?.completed : texts?.pending;
  const isExpired =
    subPackage.EndDate && new Date(subPackage.EndDate) < new Date();
  const showWarning = isExpired && !subPackage.IsApproved;
  const accentColor = showWarning
    ? "red"
    : statusColor === "green"
      ? "green"
      : "orange";
  return (
    <Card
      variant="outline"
      borderLeft="4px"
      borderLeftColor={`${accentColor}.400`}
      bg={cardBg}
      shadow="md"
      _hover={{
        shadow: "lg",
        transform: "translateY(-2px)",
      }}
      transition="all 0.2s"
      borderRadius="lg"
      overflow="hidden"
    >
      <CardBody p={4}>
        <VStack align="start" spacing={3}>
          <Flex justify="space-between" align="start" w="full">
            <Box flex={1}>
              <Text fontWeight="bold" fontSize="md" color={titleColor}>
                {subPackage.Name ||
                  subPackage?.Service?.ServiceName ||
                  texts?.unnamedService}
              </Text>
              {subPackage.Description && (
                <Text fontSize="sm" color="gray.500" mt={1} noOfLines={2}>
                  {subPackage.Description}
                </Text>
              )}
            </Box>
            <Badge
              colorScheme={showWarning ? "red" : statusColor}
              variant="solid"
              fontSize="xs"
            >
              <HStack spacing={1}>
                <Icon as={statusIcon} boxSize={3} />
                <Text>{showWarning ? texts?.expired : statusText}</Text>
              </HStack>
            </Badge>
          </Flex>
          <Divider />
          <HStack justify="space-between" w="full">
            <Box fontSize="sm" color="gray.600" fontWeight="medium">
              {texts?.endDate}:{" "}
              <Box as="span" fontWeight="medium">
                {subPackage.EndDate ? (
                  formatDate(subPackage.EndDate)
                ) : subPackage?.Service?.Contract?.EndDate ? (
                  formatDate(subPackage?.Service?.Contract?.EndDate)
                ) : (
                  <Badge
                    colorScheme={showWarning ? "red" : statusColor}
                    variant="solid"
                    fontSize="xs"
                  >
                    <HStack spacing={1}>
                      <Icon as={statusIcon} boxSize={3} />
                      <Text>{showWarning ? texts?.expired : statusText}</Text>
                    </HStack>
                  </Badge>
                )}
              </Box>
              {showWarning && " ⚠️"}
            </Box>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  );
};
